import '../scss/_index.scss'
import { useLogger } from '@omnia/ui/src/lib'
import { getBemClassBuilder } from '@omnia/ui/src/lib/lumos-plus/utils/get-bem-class-builder'
import { browsingDomainStore } from '@vista-digital/occ-domain'
import { ButtonColor, ButtonIconPosition, ModalMode } from '@vista-digital/occ-ui'
import { Button } from '@vista-digital/occ-ui/src/lib/subcomponents/button/button'
import { Modal } from '@vista-digital/occ-ui/src/lib/subcomponents/modal/modal'
import { useMountEffect } from '@vista-digital/react-tools'
import { runInAction } from 'mobx'
import { observer } from 'mobx-react'
import { type FC, useEffect, useState } from 'react'
import { getCinemaGroups } from '../utils/get-cinema-groups'
import { getSelectedCinemaGroup } from '../utils/get-selected-cinema-group'
import { getSelectedCinemaGroupSiteIds } from '../utils/get-selected-cinema-group-site-ids'
import { setSelectedCinemaGroup } from '../utils/set-selected-cinema-group'

const ComponentName = 'CinemaGroupSelector'
const b = getBemClassBuilder(ComponentName)

export const CinemaGroupSelector: FC = observer(() => {
    const logger = useLogger(ComponentName)
    const [showModal, setShowModal] = useState(false)

    const selectedGroup = getSelectedCinemaGroup()
    const cinemaGroups = getCinemaGroups()

    useMountEffect(() => {
        if (!selectedGroup) return
        const latestCmsGroup = cinemaGroups.find(group => group.id === selectedGroup.id)

        // If a cinema group has been removed from the CMS, automatically unselect it on initial page load
        if (!latestCmsGroup) {
            logger.info('Saved cinema group no longer exists in CMS, unselecting', { selectedGroup, cinemaGroups })
            setSelectedCinemaGroup(null)
            return
        }

        // If a user has a saved cinema group on initial page load, update it to the latest CMS version to ensure it has the latest cinema data
        setSelectedCinemaGroup(latestCmsGroup)
        logger.debug('Saved cinema group updated to latest CMS version', { selectedGroup, latestCmsGroup })
    })

    useEffect(() => {
        if (!selectedGroup) {
            runInAction(() => {
                // OCC does not provide any "setSelectedSiteIds"
                browsingDomainStore.data.selectedSiteIds = []
                logger.info('Selected cinema group undefined, removed any selected site ids')
            })
            return
        }

        const siteIds = getSelectedCinemaGroupSiteIds()
        runInAction(() => {
            browsingDomainStore.data.selectedSiteIds = siteIds
        })

        logger.info('Selected cinema group update, selected site ids updated', { selectedGroup, siteIds })
    }, [logger, selectedGroup])

    return (
        <>
            <Button
                className={b.element('action-button')}
                color={ButtonColor.Tertiary}
                type={'button'}
                label={selectedGroup?.name || ''}
                iconName="location-pin"
                iconPosition={ButtonIconPosition.Right}
                onClick={() => setShowModal(true)}
            />

            <Modal
                className={b.element('modal')}
                mode={ModalMode.Overlay}
                anchoredToTop={true}
                show={showModal}
                onClose={() => setShowModal(false)}
            >
                <ul className={b.element('list')}>
                    {cinemaGroups.map(cinemaGroup => (
                        <li
                            key={cinemaGroup.id}
                        >
                            <Button
                                color={ButtonColor.Tertiary}
                                label={cinemaGroup.name}
                                iconName={selectedGroup?.id === cinemaGroup.id ? 'check' : 'none'}
                                iconPosition={ButtonIconPosition.Left}
                                onClick={() => {
                                    setSelectedCinemaGroup(cinemaGroup)
                                    setShowModal(false)
                                }}
                            />
                        </li>
                    ))}
                </ul>
            </Modal>
        </>
    )
})
